// gym/GymSchedule.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField,  Alert, Snackbar, Chip,
  FormControl, InputLabel, Select, MenuItem,
  Tooltip, Card, CardContent, Avatar,
  CircularProgress, InputAdornment,
  ToggleButtonGroup, ToggleButton
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import {
  Add, Edit, Delete, AccessTime, CheckCircle,
  Cancel, Search, Refresh, Person,
  Groups, CalendarToday, FitnessCenter,
  Schedule, Today
} from '@mui/icons-material';
import api from '../services/auth';

const GymSchedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dayFilter, setDayFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table' ou 'calendar'
  const [formData, setFormData] = useState({
    class_name: '',
    coach: null,
    day_of_week: 'MON',
    start_time: '08:00',
    end_time: '09:00',
    max_capacity: 20,
    description: '',
    is_active: true
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingCoaches, setLoadingCoaches] = useState(false);

  const DAY_CHOICES = [
    { value: 'MON', label: 'Lundi', order: 1 },
    { value: 'TUE', label: 'Mardi', order: 2 },
    { value: 'WED', label: 'Mercredi', order: 3 },
    { value: 'THU', label: 'Jeudi', order: 4 },
    { value: 'FRI', label: 'Vendredi', order: 5 },
    { value: 'SAT', label: 'Samedi', order: 6 },
    { value: 'SUN', label: 'Dimanche', order: 7 }
  ];

  const DAYS_OF_WEEK = {
    'MON': { label: 'Lundi', short: 'Lun' },
    'TUE': { label: 'Mardi', short: 'Mar' },
    'WED': { label: 'Mercredi', short: 'Mer' },
    'THU': { label: 'Jeudi', short: 'Jeu' },
    'FRI': { label: 'Vendredi', short: 'Ven' },
    'SAT': { label: 'Samedi', short: 'Sam' },
    'SUN': { label: 'Dimanche', short: 'Dim' }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterSchedules();
  }, [schedules, searchTerm, statusFilter, dayFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchSchedules(),
        fetchCoaches()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des données',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedules = async () => {
    try {
      const response = await api.get('/gym/schedules/');
      console.log('Schedules response:', response.data);
      const schedulesData = Array.isArray(response.data) ? response.data : [];
      // Trier par jour et heure de début
      const sortedSchedules = schedulesData.sort((a, b) => {
        const dayOrderA = DAY_CHOICES.find(d => d.value === a.day_of_week)?.order || 0;
        const dayOrderB = DAY_CHOICES.find(d => d.value === b.day_of_week)?.order || 0;
        if (dayOrderA !== dayOrderB) return dayOrderA - dayOrderB;
        return a.start_time.localeCompare(b.start_time);
      });
      setSchedules(sortedSchedules);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setSchedules([]);
    }
  };

  const fetchCoaches = async () => {
    try {
      setLoadingCoaches(true);
      const response = await api.get('/gym/coaches/');
      const coachesData = Array.isArray(response.data) ? response.data : [];
      console.log('Coaches loaded:', coachesData.length);
      setCoaches(coachesData);
    } catch (error) {
      console.error('Error fetching coaches:', error);
      setCoaches([]);
    } finally {
      setLoadingCoaches(false);
    }
  };

  const filterSchedules = () => {
    const schedulesArray = Array.isArray(schedules) ? schedules : [];
    let filtered = schedulesArray;

    if (searchTerm) {
      filtered = filtered.filter(schedule =>
        schedule.class_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        schedule.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        schedule.coach?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        schedule.coach?.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(schedule =>
        statusFilter === 'active' ? schedule.is_active : !schedule.is_active
      );
    }

    if (dayFilter !== 'all') {
      filtered = filtered.filter(schedule => schedule.day_of_week === dayFilter);
    }

    setFilteredSchedules(filtered);
  };

  const handleOpenDialog = (schedule = null) => {
    if (schedule) {
      setEditingSchedule(schedule);
      setFormData({
        class_name: schedule.class_name || '',
        coach: schedule.coach || null,
        day_of_week: schedule.day_of_week || 'MON',
        start_time: schedule.start_time || '08:00',
        end_time: schedule.end_time || '09:00',
        max_capacity: schedule.max_capacity || 20,
        description: schedule.description || '',
        is_active: schedule.is_active !== undefined ? schedule.is_active : true
      });
    } else {
      setEditingSchedule(null);
      setFormData({
        class_name: '',
        coach: null,
        day_of_week: 'MON',
        start_time: '08:00',
        end_time: '09:00',
        max_capacity: 20,
        description: '',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSchedule(null);
    setSubmitting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Préparer les données pour l'API
      const apiData = {
        class_name: formData.class_name,
        coach_id: formData.coach?.id || null,
        day_of_week: formData.day_of_week,
        start_time: formData.start_time,
        end_time: formData.end_time,
        max_capacity: parseInt(formData.max_capacity),
        description: formData.description,
        is_active: formData.is_active
      };

      console.log('Submitting schedule data:', apiData);

      if (editingSchedule) {
        await api.put(`/gym/schedules/${editingSchedule.id}/`, apiData);
        setSnackbar({ open: true, message: 'Cours modifié avec succès', severity: 'success' });
      } else {
        await api.post('/gym/schedules/', apiData);
        setSnackbar({ open: true, message: 'Cours créé avec succès', severity: 'success' });
      }

      handleCloseDialog();
      setTimeout(() => {
        fetchSchedules();
      }, 800);

    } catch (error) {
      console.error('Error submitting schedule:', error);
      console.error('Error details:', error.response?.data);

      let errorMessage = 'Erreur lors de l\'opération';
      if (error.response?.data) {
        if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else {
          const errors = Object.values(error.response.data).flat();
          errorMessage = errors.join(', ');
        }
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce cours?')) {
      try {
        await api.delete(`/gym/schedules/${id}/`);
        setSnackbar({ open: true, message: 'Cours supprimé avec succès', severity: 'success' });
        fetchSchedules();
      } catch (error) {
        console.error('Error deleting schedule:', error);
        setSnackbar({ open: true, message: 'Erreur lors de la suppression', severity: 'error' });
      }
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      const [hours, minutes] = timeString.split(':');
      return `${hours}h${minutes}`;
    } catch (e) {
      return timeString;
    }
  };

  const getDayLabel = (value) => {
    return DAYS_OF_WEEK[value]?.label || value;
  };

  const getShortDayLabel = (value) => {
    return DAYS_OF_WEEK[value]?.short || value;
  };

  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    try {
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const [endHours, endMinutes] = endTime.split(':').map(Number);
      const startTotal = startHours * 60 + startMinutes;
      const endTotal = endHours * 60 + endMinutes;
      return (endTotal - startTotal) / 60; // en heures
    } catch (e) {
      return 0;
    }
  };

  const formatDuration = (hours) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    if (wholeHours === 0) return `${minutes}min`;
    if (minutes === 0) return `${wholeHours}h`;
    return `${wholeHours}h${minutes}`;
  };

  // Grouper les cours par jour pour la vue calendrier
  const groupedByDay = DAY_CHOICES.reduce((acc, day) => {
    acc[day.value] = filteredSchedules.filter(schedule => schedule.day_of_week === day.value);
    return acc;
  }, {});

  // Calcul des statistiques
  const stats = {
    total: Array.isArray(schedules) ? schedules.length : 0,
    active: Array.isArray(schedules) ? schedules.filter(s => s.is_active).length : 0,
    inactive: Array.isArray(schedules) ? schedules.filter(s => !s.is_active).length : 0,
    coachesCount: Array.isArray(schedules) ? 
      new Set(schedules.filter(s => s.coach).map(s => s.coach?.id)).size : 0
  };

  // Générer des plages horaires typiques
  const timeSlots = [
    '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00'
  ];

  return (
    <Box p={3}>
      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Schedule color="primary" />
                <Typography variant="h6">Total Cours</Typography>
              </Box>
              <Typography variant="h4" color="primary.main">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: 'success.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircle />
                <Typography variant="h6">Cours Actifs</Typography>
              </Box>
              <Typography variant="h4">
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: 'info.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Person />
                <Typography variant="h6">Coachs assignés</Typography>
              </Box>
              <Typography variant="h4">
                {stats.coachesCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTime />
                <Typography variant="h6">Par Semaine</Typography>
              </Box>
              <Typography variant="h4">
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">📅 Emploi du temps GYM</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={submitting || loadingCoaches}
        >
          {submitting ? <CircularProgress size={24} /> : 'Nouveau Cours'}
        </Button>
      </Box>

      {/* Barre de recherche et filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Rechercher cours, coach, description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Jour</InputLabel>
              <Select
                value={dayFilter}
                label="Jour"
                onChange={(e) => setDayFilter(e.target.value)}
              >
                <MenuItem value="all">Tous les jours</MenuItem>
                {DAY_CHOICES.map(day => (
                  <MenuItem key={day.value} value={day.value}>
                    {day.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="active">Actifs</MenuItem>
                <MenuItem value="inactive">Inactifs</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
              fullWidth
            >
              <ToggleButton value="table">
                <Typography variant="body2">Vue Tableau</Typography>
              </ToggleButton>
              <ToggleButton value="calendar">
                <Typography variant="body2">Vue Calendrier</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchData}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Actualiser'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Vue Tableau */}
      {viewMode === 'table' ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.light' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Jour</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cours</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Horaire</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Coach</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Capacité</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Statut</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Chargement des cours...</Typography>
                  </TableCell>
                </TableRow>
              ) : !Array.isArray(filteredSchedules) || filteredSchedules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    {!Array.isArray(schedules) || schedules.length === 0 ? (
                      <Box>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                          Aucun cours programmé
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Cliquez sur "Nouveau Cours" pour créer votre premier cours.
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                          Aucun cours trouvé avec les filtres actuels
                        </Typography>
                        <Button
                          variant="outlined"
                          sx={{ mt: 2 }}
                          onClick={() => {
                            setSearchTerm('');
                            setStatusFilter('all');
                            setDayFilter('all');
                          }}
                        >
                          Réinitialiser les filtres
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredSchedules.map((schedule) => (
                  <TableRow key={schedule.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Today fontSize="small" color="action" />
                        <Typography fontWeight="bold">
                          {getDayLabel(schedule.day_of_week)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography fontWeight="bold">
                          {schedule.class_name}
                        </Typography>
                        {schedule.description && (
                          <Typography variant="caption" color="textSecondary">
                            {schedule.description.substring(0, 50)}...
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <AccessTime fontSize="small" color="action" />
                          <Typography>
                            {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                          {formatDuration(calculateDuration(schedule.start_time, schedule.end_time))}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {schedule.coach ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 30, height: 30, fontSize: '0.8rem' }}>
                            {schedule.coach.first_name?.[0]}{schedule.coach.last_name?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">
                              {schedule.coach.first_name} {schedule.coach.last_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {schedule.coach.speciality}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Non assigné
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Groups fontSize="small" color="action" />
                        <Typography>
                          {schedule.max_capacity} places
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={schedule.is_active ? 'Actif' : 'Inactif'}
                        color={schedule.is_active ? 'success' : 'error'}
                        size="small"
                        icon={schedule.is_active ? <CheckCircle /> : <Cancel />}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Modifier">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleOpenDialog(schedule)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDelete(schedule.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        // Vue Calendrier
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            📋 Emploi du temps hebdomadaire
          </Typography>
          <Grid container spacing={2}>
            {DAY_CHOICES.map(day => {
              const daySchedules = groupedByDay[day.value] || [];
              return (
                <Grid item xs={12} key={day.value}>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Today color="primary" />
                      <Typography variant="h6">
                        {day.label}
                      </Typography>
                      <Chip 
                        label={`${daySchedules.length} cours`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </Box>
                    {daySchedules.length === 0 ? (
                      <Typography color="textSecondary" align="center" py={2}>
                        Aucun cours programmé ce jour
                      </Typography>
                    ) : (
                      <Grid container spacing={1}>
                        {daySchedules.map(schedule => (
                          <Grid item xs={12} sm={6} md={4} key={schedule.id}>
                            <Card variant="outlined" sx={{ height: '100%' }}>
                              <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {schedule.class_name}
                                  </Typography>
                                  <Chip
                                    label={schedule.is_active ? 'Actif' : 'Inactif'}
                                    color={schedule.is_active ? 'success' : 'error'}
                                    size="small"
                                  />
                                </Box>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  <AccessTime fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    ({formatDuration(calculateDuration(schedule.start_time, schedule.end_time))})
                                  </Typography>
                                </Box>
                                {schedule.coach && (
                                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <Person fontSize="small" color="action" />
                                    <Typography variant="body2">
                                      {schedule.coach.first_name} {schedule.coach.last_name}
                                    </Typography>
                                  </Box>
                                )}
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Groups fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    {schedule.max_capacity} places
                                  </Typography>
                                </Box>
                                {schedule.description && (
                                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    {schedule.description.substring(0, 80)}...
                                  </Typography>
                                )}
                                <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                                  <Tooltip title="Modifier">
                                    <IconButton
                                      color="primary"
                                      size="small"
                                      onClick={() => handleOpenDialog(schedule)}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Supprimer">
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() => handleDelete(schedule.id)}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Dialog pour créer/modifier */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSchedule ? '✏️ Modifier Cours' : '➕ Nouveau Cours'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nom du cours *"
                  value={formData.class_name}
                  onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
                  required
                  margin="normal"
                  placeholder="Ex: Yoga matinal, CrossFit soir"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Jour de la semaine *</InputLabel>
                  <Select
                    value={formData.day_of_week}
                    label="Jour de la semaine *"
                    onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
                  >
                    {DAY_CHOICES.map(day => (
                      <MenuItem key={day.value} value={day.value}>
                        {day.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Heure de début *"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Heure de fin *"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  required
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Coach (optionnel)</InputLabel>
                  <Select
                    value={formData.coach?.id || ''}
                    label="Coach (optionnel)"
                    onChange={(e) => {
                      const coach = coaches.find(c => c.id === e.target.value);
                      setFormData({ ...formData, coach: coach || null });
                    }}
                    disabled={loadingCoaches}
                  >
                    <MenuItem value="">
                      <em>Aucun coach</em>
                    </MenuItem>
                    {coaches.map(coach => (
                      <MenuItem key={coach.id} value={coach.id}>
                        {coach.first_name} {coach.last_name} ({coach.speciality})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Capacité maximale *"
                  type="number"
                  value={formData.max_capacity}
                  onChange={(e) => setFormData({ ...formData, max_capacity: e.target.value })}
                  required
                  margin="normal"
                  inputProps={{ min: 1, max: 100 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description (optionnel)"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                  margin="normal"
                  placeholder="Description détaillée du cours..."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.is_active}
                    label="Statut"
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                  >
                    <MenuItem value={true}>Actif</MenuItem>
                    <MenuItem value={false}>Inactif</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* {formData.start_time && formData.end_time && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Récapitulatif :
                    </Typography>
                    <Typography variant="body2">
                      {formData.class_name || "Nouveau cours"} le {getDayLabel(formData.day_of_week)} 
                      de {formatTime(formData.start_time)} à {formatTime(formData.end_time)}
                    </Typography>
                    {formData.coach && (
                      <Typography variant="body2">
                        Coach : {formData.coach.first_name} {formData.coach.last_name}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      Capacité : {formData.max_capacity} places
                    </Typography>
                  </Paper>
                </Grid>
              )} */}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={submitting}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting || !formData.class_name || !formData.start_time || !formData.end_time}
            >
              {submitting ? <CircularProgress size={24} /> : editingSchedule ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GymSchedule;